/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { If, Then, Else } from "react-if";
import { Link } from "react-router-dom";
import { FlexContainer, Text, theme } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { ColorBadge, LegendItemWrap, ValueTag } from "./styles";
import { StatusChartItem } from "./types";

type Props = {
  items: StatusChartItem[];
  loading?: boolean;
  direction?: "row" | "column";
  itemsPerColumn?: number;
  fontResponsivePoint?: number;
  itemWidthCh?: number;
};

export function ChartLegend({
  items,
  loading = false,
  direction = "column",
  itemsPerColumn,
  fontResponsivePoint,
  itemWidthCh,
}: Props) {
  const renderLegendItem = ({ label, color, count, link }: StatusChartItem) => (
    <LegendItemWrap
      key={label}
      fontResponsivePoint={fontResponsivePoint}
      widthCh={itemWidthCh}
    >
      <ColorBadge className="is-sm-visible" color={color} />
      <ValueTag className="is-md-visible" color={color}>
        {loading ? "" : count}
      </ValueTag>
      <If condition={isNotNil(link)}>
        <Then>
          <Link to={link ?? ""}>{label}</Link>
        </Then>
        <Else>{label}</Else>
      </If>
      <Text className="is-sm-visible" weight="bold" mb={0}>
        {loading ? "" : count}
      </Text>
    </LegendItemWrap>
  );

  const shouldSplitIntoColumns =
    direction === "column" && isNotNil(itemsPerColumn) && itemsPerColumn > 0;

  if (!shouldSplitIntoColumns) {
    return (
      <FlexContainer
        alignItems="flex-start"
        wrap="wrap"
        css={{
          gap: `${theme.spacing.spacing02} ${theme.spacing.spacing07}`,
          ...(fontResponsivePoint && {
            [`@media (max-width: ${fontResponsivePoint}px)`]: {
              gap: 0,
            },
          }),
        }}
      >
        {items.map(renderLegendItem)}
      </FlexContainer>
    );
  }

  const columnSize = Math.floor(itemsPerColumn);
  const columnCount = columnSize > 0 ? Math.ceil(items.length / columnSize) : 0;

  const columns: StatusChartItem[][] = Array.from(
    { length: columnCount },
    (_, columnIndex) => {
      const start = columnIndex * columnSize;
      return items.slice(start, start + columnSize);
    }
  );

  return (
    <FlexContainer
      direction="row"
      alignItems="flex-start"
      gap={theme.spacing.spacing07}
    >
      {columns.map((columnItems, columnIndex) => (
        <FlexContainer
          key={`legend-column-${columnIndex}`}
          direction="column"
          alignItems="flex-start"
          gap={theme.spacing.spacing05}
        >
          {columnItems.map(renderLegendItem)}
        </FlexContainer>
      ))}
    </FlexContainer>
  );
}
